import { createEventFromTemplateRpc } from '@/lib/supabase/rpc';
import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/types/database';
import type { EventBuilderDraft, EventBuilderPartyDraft, EventBuilderSlotDraft, EventStatus } from '@/types/domain';
import { createEmptyPartyDrafts, normalizePartySlots } from '@/features/events/builder';

type EventInsert = Database['public']['Tables']['events']['Insert'];
type EventUpdate = Database['public']['Tables']['events']['Update'];
type EventPartyInsert = Database['public']['Tables']['event_parties']['Insert'];
type EventPartyUpdate = Database['public']['Tables']['event_parties']['Update'];
type EventSlotInsert = Database['public']['Tables']['event_slots']['Insert'];
type EventSlotUpdate = Database['public']['Tables']['event_slots']['Update'];

function assertSupabase() {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  return supabase;
}

async function getCurrentAccessToken(): Promise<string> {
  const client = assertSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Sign in again before publishing Discord notifications.');
  }

  return session.access_token;
}

function shouldTriggerImmediatePublishNotification(status: EventStatus): boolean {
  return status === 'upcoming' || status === 'open';
}

async function triggerImmediatePublishNotification(eventId: string): Promise<void> {
  const accessToken = await getCurrentAccessToken();
  const response = await fetch('/api/admin/process-event-publish-dm', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      eventId,
    }),
  });

  if (response.ok) {
    return;
  }

  const payload = (await response.json().catch(() => null)) as { error?: string } | null;
  throw new Error(payload?.error ?? 'Failed to trigger the immediate publish Discord DMs.');
}

function normalizeParties(parties: EventBuilderPartyDraft[]): EventBuilderPartyDraft[] {
  const defaultParties = createEmptyPartyDrafts();

  return defaultParties.map((defaultParty) => {
    const party = parties.find((entry) => entry.partyNumber === defaultParty.partyNumber) ?? defaultParty;

    return {
      ...party,
      label: party.label.trim() || defaultParty.label,
      sortOrder: defaultParty.partyNumber,
      slots: normalizePartySlots(
        [...party.slots].sort((left, right) => left.slotOrder - right.slotOrder).map((slot) => ({
          ...slot,
          partyNumber: defaultParty.partyNumber,
        })),
      ),
    };
  });
}

function toEventInsertValues(draft: EventBuilderDraft, actorProfileId: string): EventInsert {
  return {
    title: draft.title.trim(),
    description: draft.description.trim() || null,
    template_id: draft.templateId ?? null,
    status: draft.status,
    created_by: actorProfileId,
    updated_by: actorProfileId,
    starts_at: draft.startsAt,
    signup_opens_at: draft.signupOpensAt,
    signup_closes_at: draft.signupClosesAt,
    waitlist_enabled: draft.waitlistEnabled,
    party_count: 4,
  };
}

function toEventUpdateValues(draft: EventBuilderDraft, actorProfileId: string): EventUpdate {
  return {
    title: draft.title.trim(),
    description: draft.description.trim() || null,
    template_id: draft.templateId ?? null,
    status: draft.status,
    updated_by: actorProfileId,
    starts_at: draft.startsAt,
    signup_opens_at: draft.signupOpensAt,
    signup_closes_at: draft.signupClosesAt,
    waitlist_enabled: draft.waitlistEnabled,
    party_count: 4,
  };
}

function toSlotValues(slot: EventBuilderSlotDraft, eventId: string, eventPartyId: string): EventSlotInsert & EventSlotUpdate {
  return {
    event_id: eventId,
    event_party_id: eventPartyId,
    template_slot_id: slot.templateSlotId ?? null,
    party_number: slot.partyNumber,
    slot_order: slot.slotOrder,
    role_name: slot.roleCategory,
    slot_note: slot.note.trim() || null,
    role_category: slot.roleCategory,
    weapon_catalog_id: slot.weaponId ?? null,
    weapon_name: slot.weaponName.trim(),
    weapon_image_path: slot.imagePath.trim(),
    capacity: slot.capacity,
    waitlist_enabled: slot.waitlistEnabled,
    is_active: slot.isActive,
  };
}

async function ensureParties(
  eventId: string,
  parties: EventBuilderPartyDraft[],
): Promise<Map<number, { id: string; partyNumber: number }>> {
  const client = assertSupabase();
  const { data: existingParties, error: existingError } = await client
    .from('event_parties')
    .select('id, party_number')
    .eq('event_id', eventId);

  if (existingError) {
    throw new Error(existingError.message);
  }

  const partiesByNumber = new Map<number, { id: string; partyNumber: number }>(
    (existingParties ?? []).map((party) => [party.party_number, { id: party.id, partyNumber: party.party_number }]),
  );

  const missingParties = parties
    .filter((party) => !partiesByNumber.has(party.partyNumber))
    .map<EventPartyInsert>((party) => ({
      event_id: eventId,
      party_number: party.partyNumber,
      label: party.label,
      sort_order: party.sortOrder,
    }));

  if (missingParties.length > 0) {
    const { data: insertedParties, error: insertError } = await client
      .from('event_parties')
      .insert(missingParties)
      .select('id, party_number');

    if (insertError) {
      throw new Error(insertError.message);
    }

    for (const party of insertedParties ?? []) {
      partiesByNumber.set(party.party_number, { id: party.id, partyNumber: party.party_number });
    }
  }

  for (const party of parties) {
    const existing = partiesByNumber.get(party.partyNumber);

    if (!existing) {
      throw new Error(`Missing Party ${party.partyNumber} for event builder save.`);
    }

    const { error } = await client
      .from('event_parties')
      .update({
        label: party.label,
        sort_order: party.sortOrder,
      } satisfies EventPartyUpdate)
      .eq('id', existing.id);

    if (error) {
      throw new Error(error.message);
    }
  }

  return partiesByNumber;
}

async function syncEventSlots(eventId: string, parties: EventBuilderPartyDraft[]): Promise<void> {
  const client = assertSupabase();
  const partyMap = await ensureParties(eventId, parties);
  const { data: existingSlots, error: existingError } = await client
    .from('event_slots')
    .select('id, party_number')
    .eq('event_id', eventId);

  if (existingError) {
    throw new Error(existingError.message);
  }

  const existingSlotIds = new Set((existingSlots ?? []).map((slot) => slot.id));
  const nextSlots = parties.flatMap((party) => party.slots);
  const nextIds = new Set(nextSlots.map((slot) => slot.id).filter((id): id is string => Boolean(id)));
  const removableIds = (existingSlots ?? []).filter((slot) => !nextIds.has(slot.id)).map((slot) => slot.id);

  if (removableIds.length > 0) {
    const { data: historicalSignups, error: signupError } = await client
      .from('signups')
      .select('event_slot_id')
      .in('event_slot_id', removableIds);

    if (signupError) {
      throw new Error(signupError.message);
    }

    const protectedSlotIds = new Set((historicalSignups ?? []).map((signup) => signup.event_slot_id));
    const inactiveIds = removableIds.filter((id) => protectedSlotIds.has(id));
    const deleteIds = removableIds.filter((id) => !protectedSlotIds.has(id));

    if (inactiveIds.length > 0) {
      const { error } = await client.from('event_slots').update({ is_active: false }).in('id', inactiveIds);

      if (error) {
        throw new Error(error.message);
      }
    }

    if (deleteIds.length > 0) {
      const { error } = await client.from('event_slots').delete().in('id', deleteIds);

      if (error) {
        throw new Error(error.message);
      }
    }
  }

  for (const party of parties) {
    const eventParty = partyMap.get(party.partyNumber);

    if (!eventParty) {
      throw new Error(`Missing Party ${party.partyNumber} while syncing slots.`);
    }

    for (const slot of party.slots) {
      const values = toSlotValues(slot, eventId, eventParty.id);

      if (slot.id && existingSlotIds.has(slot.id)) {
        const { error } = await client.from('event_slots').update(values).eq('id', slot.id);

        if (error) {
          throw new Error(error.message);
        }

        continue;
      }

      const { error } = await client.from('event_slots').insert(values);

      if (error) {
        throw new Error(error.message);
      }
    }
  }
}

export async function saveEventBuilderDraft(draft: EventBuilderDraft, actorProfileId: string): Promise<string> {
  const client = assertSupabase();
  const normalizedDraft: EventBuilderDraft = {
    ...draft,
    parties: normalizeParties(draft.parties),
  };

  if (normalizedDraft.id) {
    const { error } = await client.from('events').update(toEventUpdateValues(normalizedDraft, actorProfileId)).eq('id', normalizedDraft.id);

    if (error) {
      throw new Error(error.message);
    }

    await syncEventSlots(normalizedDraft.id, normalizedDraft.parties);

    if (shouldTriggerImmediatePublishNotification(normalizedDraft.status)) {
      await triggerImmediatePublishNotification(normalizedDraft.id).catch((error) => {
        console.warn('[Woof Woof] Immediate publish Discord DMs failed:', error);
      });
    }

    return normalizedDraft.id;
  }

  const { data, error } = await client
    .from('events')
    .insert(toEventInsertValues(normalizedDraft, actorProfileId))
    .select('id')
    .single();

  if (error || !data) {
    throw new Error(error?.message ?? 'Failed to create event.');
  }

  try {
    await syncEventSlots(data.id, normalizedDraft.parties);

    if (shouldTriggerImmediatePublishNotification(normalizedDraft.status)) {
      await triggerImmediatePublishNotification(data.id).catch((error) => {
        console.warn('[Woof Woof] Immediate publish Discord DMs failed:', error);
      });
    }

    return data.id;
  } catch (saveError) {
    await client.from('events').delete().eq('id', data.id);
    throw saveError;
  }
}

export interface CreateEventFromTemplateQuickPayload {
  templateId: string;
  title: string;
  description?: string;
  startsAt: string;
  signupOpensAt: string;
  signupClosesAt: string;
}

export async function createEventFromTemplateQuick(payload: CreateEventFromTemplateQuickPayload): Promise<string> {
  const { data, error } = await createEventFromTemplateRpc({
    templateId: payload.templateId,
    title: payload.title,
    description: payload.description ?? '',
    startsAt: payload.startsAt,
    signupOpensAt: payload.signupOpensAt,
    signupClosesAt: payload.signupClosesAt,
  });

  if (error || typeof data !== 'string') {
    throw new Error(error?.message ?? 'Failed to create event from template.');
  }

  await triggerImmediatePublishNotification(data).catch((error) => {
    console.warn('[Woof Woof] Immediate publish Discord DMs failed:', error);
  });

  return data;
}
